import { useState, useEffect } from 'react';
import {
  Button,
  Progress,
  Badge,
  Spinner,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from '@rnd-payplus/alloy-react';
import { Upload, Eye, Send, Info } from 'lucide-react';
import { toast } from 'sonner';
import { KPICard } from '@rnd-payplus/alloy-react/patterns';
import { getCurrentTenantId } from '../utils/auth';
import { PLATFORMS } from '../constants/platforms';
import { usePageShell } from '../contexts/PageShellContext';

const InvoiceOCRPage = () => {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [selectedInvoice, setSelectedInvoice] = useState<any>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [destination, setDestination] = useState('');
  const [tenantId, setTenantId] = useState(getCurrentTenantId());
  const [tenants, setTenants] = useState<any[]>([]);
  const [destinations, setDestinations] = useState<{ value: string; label: string }[]>([]);

  useEffect(() => {
    loadInvoices();
    loadTenants();
  }, []);

  useEffect(() => {
    if (!tenantId) { setDestinations([]); return; }
    fetch(`/api/connections?tenantId=${tenantId}`)
      .then((r) => (r.ok ? r.json() : []))
      .then((conns: any[]) => {
        const active = conns
          .filter((c) => c.status === 'connected' || c.status === 'active')
          .map((c) => ({ value: c.platform, label: c.name }));
        setDestinations(active.length > 0 ? active : PLATFORMS.map((p) => ({ value: p.label, label: p.label })));
        if (!destination && active.length > 0) setDestination(active[0].value);
      })
      .catch(() => setDestinations(PLATFORMS.map((p) => ({ value: p.label, label: p.label }))));
  }, [tenantId]);

  const loadTenants = async () => {
    try {
      const response = await fetch('/api/tenants');
      if (!response.ok) throw new Error('Failed to load tenants');
      const data = await response.json();
      setTenants(data);
      if (!tenantId && data.length > 0) setTenantId(data[0].id);
    } catch (error) {
      console.error('Error loading tenants:', error);
      setTenants([]);
    }
  };

  const loadInvoices = async () => {
    try {
      const response = await fetch('/api/invoices');
      if (!response.ok) throw new Error(`Failed to load invoices (${response.status})`);
      setInvoices(await response.json());
    } catch (error) {
      console.error('Error loading invoices:', error);
      setInvoices([]);
      toast.error(error instanceof Error ? error.message : 'Failed to load invoices');
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setFile(event.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('tenantId', tenantId);
      formData.append('destination', destination);

      const response = await fetch('/api/invoices/upload', {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();

      if (result.success) {
        toast.success('Invoice processed!', {
          description: `Invoice ID: ${result.invoiceId} | Confidence: ${result.confidence}% | Invoice #: ${result.extractedData.invoiceNumber || 'N/A'} | Supplier: ${result.extractedData.supplierName || 'N/A'} | Amount: ${result.extractedData.currency || ''} ${result.extractedData.totalAmount || 'N/A'}`,
        });
        setFile(null);
        loadInvoices();
      } else {
        toast.error('Failed to process invoice');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Upload failed');
    }

    setUploading(false);
  };

  const handleRoute = async (invoiceId: string) => {
    try {
      const response = await fetch(`/api/invoices/${invoiceId}/route`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ destination: 'Tungsten Pay+' })
      });
      if (!response.ok) throw new Error(`Failed to route invoice (${response.status})`);
      const result = await response.json();
      toast.success(result.message);
      loadInvoices();
    } catch (error) {
      console.error('Routing error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to route invoice');
    }
  };

  const viewDetails = (invoice: any) => {
    setSelectedInvoice(invoice);
    setDetailsOpen(true);
  };

  const totalInvoices = invoices.length;
  const processed = invoices.filter((i: any) => i.ocr_status === 'completed').length;
  const failed = invoices.filter((i: any) => i.ocr_status === 'failed').length;
  const avgConfidence = totalInvoices > 0
    ? Math.round(invoices.reduce((sum: number, i: any) => sum + (i.ocr_confidence ? parseFloat(i.ocr_confidence) * 100 : 0), 0) / totalInvoices) + '%'
    : '0%';

  usePageShell({
    title: 'Invoice OCR & Routing',
    breadcrumbs: [
      { label: 'Home', href: '/dashboard' },
      { label: 'Invoice OCR' },
    ],
  });

  return (
    <div>
      {/* KPI Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <KPICard title="Total" value={totalInvoices} subtitle="Invoices" />
        <KPICard title="Processed" value={processed} subtitle="Invoices" />
        <KPICard title="Avg Confidence" value={avgConfidence} subtitle="OCR" />
        <KPICard title="Failed" value={failed} subtitle="Invoices" />
      </div>

      {/* Upload Form */}
      <div className="mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label htmlFor="tenant-select" className="block text-sm font-medium mb-1.5">
                Tenant
              </label>
              <select
                id="tenant-select"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={tenantId}
                onChange={(e) => setTenantId(e.target.value)}
              >
                {tenants.length === 0 && <option value="" disabled>Loading tenants...</option>}
                {tenants.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="destination-select" className="block text-sm font-medium mb-1.5">
                Destination
              </label>
              <select
                id="destination-select"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={destination}
                onChange={(e) => setDestination(e.target.value)}
              >
                {destinations.map((d) => (
                  <option key={d.value} value={d.value}>{d.label}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col">
              <label className="block text-sm font-medium mb-1.5">&nbsp;</label>
              <input
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.tiff"
                onChange={handleFileChange}
                className="hidden"
                id="invoice-upload"
              />
              <label htmlFor="invoice-upload">
                <Button variant="outline" className="w-full" asChild>
                  <span>Choose Invoice File</span>
                </Button>
              </label>
            </div>
          </div>

          {file && (
            <div className="mt-4">
              <div className="flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
                <Info className="h-4 w-4 shrink-0" />
                Selected: {file.name} ({(file.size / 1024).toFixed(0)} KB)
              </div>
              <Button
                onClick={handleUpload}
                disabled={uploading}
                className="w-full mt-4"
              >
                {uploading ? (
                  <>
                    <Spinner className="mr-2 h-4 w-4" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    Upload &amp; OCR
                  </>
                )}
              </Button>
              {uploading && <Progress value={undefined} className="mt-2" />}
            </div>
          )}
      </div>

      {/* Invoices List */}
      <div>
        <h2 className="text-base font-medium leading-6 mb-4">Processed Invoices ({invoices.length})</h2>
        <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice #</TableHead>
                  <TableHead>Supplier</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>OCR Status</TableHead>
                  <TableHead>Confidence</TableHead>
                  <TableHead>Destination</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map((invoice) => (
                  <TableRow key={invoice.id}>
                    <TableCell>{invoice.invoice_number || invoice.id}</TableCell>
                    <TableCell>{invoice.supplier_name || '-'}</TableCell>
                    <TableCell>
                      {invoice.currency} {invoice.total_amount ? parseFloat(invoice.total_amount).toFixed(2) : '-'}
                    </TableCell>
                    <TableCell>{invoice.invoice_date || '-'}</TableCell>
                    <TableCell>
                      <Badge variant={invoice.ocr_status === 'completed' ? 'default' : 'secondary'}>
                        {invoice.ocr_status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {invoice.ocr_confidence ? `${parseFloat(invoice.ocr_confidence).toFixed(0)}%` : '-'}
                    </TableCell>
                    <TableCell>{invoice.destination_system}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => viewDetails(invoice)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        {invoice.destination_status === 'pending' && (
                          <Button variant="ghost" size="sm" onClick={() => handleRoute(invoice.id)}>
                            <Send className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {invoices.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center">
                      <p className="text-muted-foreground">
                        No invoices uploaded yet. Upload an invoice to get started.
                      </p>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
        </div>
      </div>

      {/* Invoice Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Invoice Details</DialogTitle>
          </DialogHeader>
          {selectedInvoice && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Invoice Number</p>
                <p>{selectedInvoice.invoice_number || '-'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Date</p>
                <p>{selectedInvoice.invoice_date || '-'}</p>
              </div>
              <div className="col-span-2">
                <p className="text-sm font-medium text-muted-foreground">Supplier</p>
                <p>{selectedInvoice.supplier_name || '-'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Subtotal</p>
                <p>{selectedInvoice.subtotal || '-'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Tax</p>
                <p>{selectedInvoice.tax_amount || '-'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total</p>
                <p className="font-semibold">
                  {selectedInvoice.currency} {selectedInvoice.total_amount ? parseFloat(selectedInvoice.total_amount).toFixed(2) : '-'}
                </p>
              </div>
              <div className="col-span-2">
                <p className="text-sm font-medium text-muted-foreground">OCR Confidence</p>
                <Progress
                  value={selectedInvoice.ocr_confidence ? parseFloat(selectedInvoice.ocr_confidence) : 0}
                  className="mt-2"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {selectedInvoice.ocr_confidence ? `${parseFloat(selectedInvoice.ocr_confidence).toFixed(0)}%` : 'N/A'}
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Close</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InvoiceOCRPage;
